// ─────────────────────────────────────────────────────────────────────────────
// RangeSelectionTracker
// ─────────────────────────────────────────────────────────────────────────────
// Storage-agnostic helper that turns a single checkbox activation into the list
// of rows the toggle should apply to, implementing the platform-standard
// gestures users expect from a multi-select table:
//
//   - Click                 → the clicked row only. Becomes the new anchor.
//   - Shift + click         → every row between the anchor and the clicked row
//                             (inclusive). All of them adopt the clicked row's
//                             resulting state, so shift-clicking into a selected
//                             block deselects it. The anchor moves to the clicked
//                             row, so consecutive shift-clicks keep extending.
//   - Ctrl/Cmd + click      → the clicked row only, and the anchor is left where
//                             it was, so you can punch a hole in a selection and
//                             still shift-extend from the original anchor.
//   - Shift with no anchor   → behaves as a plain click.
//
// The tracker holds no row data, only an index, so it works with every storage
// shape used across the app (id arrays, Sets, arrays of entities, flags on rows).
// Callers own the list of *selectable* rows and pass it in on each activation,
// which keeps the tracker correct when rows are filtered, sorted or paginated.
//
// Usage:
//   private readonly rangeSelection = new RangeSelectionTracker();
//
//   toggleOne(row: IRow, checked: boolean, modifiers = NO_SELECTION_MODIFIERS): void {
//     const rows = this.selectableRows;
//     const affected = this.rangeSelection.resolve(rows, rows.indexOf(row), modifiers);
//     affected.forEach((r) => this.applySelection(r, checked));
//   }
// ─────────────────────────────────────────────────────────────────────────────

import { NO_SELECTION_MODIFIERS, SelectionModifiers } from '../models/selection.models';

export class RangeSelectionTracker {
  /** Index of the last plain/shift activation, used as the range pivot. */
  private anchorIndex: number | null = null;

  /**
   * Resolve the rows a checkbox activation applies to.
   *
   * @param rows      The selectable rows, in the order they are rendered.
   * @param rowIndex  Index of the activated row inside `rows` (-1 when unknown).
   * @param modifiers Keyboard modifiers held during the activation.
   * @returns The rows to apply the new checked state to, never null/undefined.
   */
  resolve<T>(rows: T[], rowIndex: number, modifiers: SelectionModifiers = NO_SELECTION_MODIFIERS): T[] {
    if (rowIndex < 0 || rowIndex >= rows.length) return [];

    // Ctrl/Cmd toggles a single row and deliberately preserves the anchor.
    if (modifiers.ctrlKey) return [rows[rowIndex]];

    const canExtend = modifiers.shiftKey && this.anchorIndex !== null && this.anchorIndex < rows.length;
    const from = canExtend ? Math.min(this.anchorIndex!, rowIndex) : rowIndex;
    const to = canExtend ? Math.max(this.anchorIndex!, rowIndex) : rowIndex;

    this.anchorIndex = rowIndex;

    return rows.slice(from, to + 1);
  }

  /**
   * Forget the anchor. Call this whenever the rendered rows change enough that
   * the stored index no longer points at the same row — new page, new filter,
   * new sort, or a full selection reset.
   */
  reset(): void {
    this.anchorIndex = null;
  }
}
